import { MAX_AGE, MIN_AGE, PhoneSchema, type OnboardingInput } from '@otryad/shared';
import { requestContact } from '@telegram-apps/sdk-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useHaptic, useTma } from '@/app/TmaProvider';
import { Button, Card, CheckRow, Field, MainButton, TextInput } from '@/components/ui';
import { draftStore, type OnboardingDraft } from '@/lib/draft';
import { errorText, fmt, PLURAL_YEARS, plural, texts } from '@/lib/texts';
import { trpc } from '@/lib/trpc';

import { OnboardingScreen } from './shared';

const t = texts.onboarding.about;

export function draftToInput(draft: OnboardingDraft): OnboardingInput | null {
  if (!draft.cityId || !draft.availability || draft.birthYear === undefined) return null;
  const levels = Object.entries(draft.levels).map(([activityId, level]) => ({ activityId, level }));
  const primary = draft.waitingForProposal ? null : (draft.primaryActivityId ?? null);
  return {
    cityId: draft.cityId,
    districtIds: draft.districtIds,
    availability: draft.availability,
    primaryActivityId: primary,
    squadSize: primary ? (draft.squadSize ?? null) : null,
    levels,
    interestActivityIds: draft.interestActivityIds,
    // Дополнительные наборы: недособранные (без размера) не отправляем — сервер их отклонит.
    extraChoices: Object.entries(draft.extraChoices)
      .filter(([id, size]) => Boolean(size) && id !== primary)
      .map(([activityId, squadSize]) => ({ activityId, squadSize: squadSize! })),
    birthYear: draft.birthYear,
    phone: draft.phone || undefined,
    agreedRules: true,
    agreedPrivacy: true,
  };
}

/** Шаг 5 — год рождения, телефон (`requestContact`), правила состава, отправка. */
export function AboutScreen() {
  return (
    <OnboardingScreen step={5} title={t.title} subtitle={t.subtitle}>
      {({ draft, update }) => <Body draft={draft} update={update} />}
    </OnboardingScreen>
  );
}

function Body({
  draft,
  update,
}: {
  draft: OnboardingDraft;
  update: (p: Partial<OnboardingDraft>) => void;
}) {
  const navigate = useNavigate();
  const haptic = useHaptic();
  const { mocked } = useTma();
  const utils = trpc.useUtils();
  const submit = trpc.onboarding.submit.useMutation();
  const acceptReserve = trpc.squad.reserve.acceptInvite.useMutation();
  const [manualPhone, setManualPhone] = useState(Boolean(draft.phone) || mocked);
  const [contactDeclined, setContactDeclined] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const year = new Date().getUTCFullYear();
  const minYear = year - MAX_AGE;
  const maxYear = year - MIN_AGE;
  const yearOk =
    draft.birthYear !== undefined && draft.birthYear >= minYear && draft.birthYear <= maxYear;
  const phoneOk = !draft.phone || PhoneSchema.safeParse(draft.phone).success;
  const canSubmit = yearOk && phoneOk && draft.agreedRules && draft.agreedPrivacy && !submit.isPending;
  /*
   * Главная кнопка живёт внизу клиента Telegram, далеко от полей: пока она неактивна, экран
   * обязан назвать первое незакрытое требование. Раньше молчал обо всём, кроме правил.
   */
  // Год введён, но вне диапазона: «Укажи год рождения» при указанном годе сбивало (раунд 3, N6).
  // Слишком молодому говорим про возраст, остальным — какие годы подходят.
  const yearError =
    draft.birthYear === undefined || yearOk
      ? null
      : draft.birthYear > maxYear && draft.birthYear <= year
        ? fmt(t.yearTooYoung, { age: MIN_AGE })
        : fmt(t.yearRange, { min: minYear, max: maxYear });
  const blocker = !yearOk
    ? (yearError ?? t.needYear)
    : !phoneOk
      ? t.phoneInvalid
      : !draft.agreedRules
        ? t.needRules
        : !draft.agreedPrivacy
          ? t.needPrivacy
          : null;
  const contactAvailable = requestContact.isAvailable();

  const askContact = async () => {
    try {
      const c = await requestContact();
      update({ phone: c.contact.phone_number });
      haptic.notify('success');
    } catch {
      setContactDeclined(true);
      setManualPhone(true);
    }
  };

  const onSubmit = async () => {
    setError(null);
    const input = draftToInput(draft);
    if (!input) return;
    try {
      const res = await submit.mutateAsync(input);
      haptic.notify('success');
      const reserveToken = draft.reserveToken;
      await draftStore.clear();
      if (reserveToken) {
        // Пришёл по ссылке запасного: занять место сразу после пяти вопросов (Stage 6, ADR-10)
        let target = `/squad/reserve?token=${encodeURIComponent(reserveToken)}`;
        try {
          await acceptReserve.mutateAsync({ token: reserveToken });
          target = '/squad/reserve?accepted=1';
        } catch {
          /* причина покажется на экране приглашения */
        }
        await Promise.all([utils.auth.me.invalidate(), utils.squad.invalidate()]);
        void navigate(target, { replace: true });
        return;
      }
      await Promise.all([utils.auth.me.invalidate(), utils.pool.invalidate()]);
      void navigate(res.state === 'inSquad' ? '/home' : '/pool', { replace: true });
    } catch (e) {
      haptic.notify('error');
      const reason = (e as { data?: { reason?: string } }).data?.reason;
      const message = (e as { message?: string }).message;
      setError(errorText(reason, message || texts.common.errorBody));
    }
  };

  return (
    <>
      <Card>
        <Field
          label={t.birthYearLabel}
          hint={
            yearOk && draft.birthYear
              ? fmt(t.ageHint, { age: plural(year - draft.birthYear, PLURAL_YEARS) })
              : `${minYear}–${maxYear}`
          }
          error={yearError ?? undefined}
        >
          <TextInput
            type="number"
            inputMode="numeric"
            placeholder={t.birthYearPlaceholder}
            min={minYear}
            max={maxYear}
            value={draft.birthYear ?? ''}
            invalid={draft.birthYear !== undefined && !yearOk}
            onChange={(e) => {
              const v = e.target.value;
              update({ birthYear: v === '' ? undefined : Number(v) });
            }}
          />
        </Field>
      </Card>

      <Card title={t.phoneLabel}>
        <p className="text-muted mb-3 text-sm">{t.phoneHint}</p>
        {draft.phone && !manualPhone ? (
          <div className="flex items-center justify-between gap-3">
            <span className="font-medium">{t.phoneShared}</span>
            <Button variant="ghost" onClick={() => setManualPhone(true)}>
              {texts.profile.edit}
            </Button>
          </div>
        ) : null}
        {!draft.phone && !manualPhone ? (
          <div className="flex flex-col gap-2">
            {contactAvailable ? (
              <Button variant="secondary" fullWidth onClick={() => void askContact()}>
                {t.requestContact}
              </Button>
            ) : null}
            <Button variant="ghost" fullWidth onClick={() => setManualPhone(true)}>
              {t.phoneManual}
            </Button>
          </div>
        ) : null}
        {manualPhone ? (
          <Field
            hint={contactDeclined ? t.phoneDeclined : undefined}
            error={!phoneOk ? t.phoneInvalid : undefined}
          >
            <TextInput
              type="tel"
              inputMode="tel"
              placeholder={t.phonePlaceholder}
              value={draft.phone ?? ''}
              invalid={!phoneOk}
              onChange={(e) =>
                update({ phone: e.target.value.replace(/[\s()-]/g, '') || undefined })
              }
            />
          </Field>
        ) : null}
      </Card>

      <Card title={t.rulesTitle}>
        <ul className="text-fg mb-2 flex list-disc flex-col gap-1 pl-5 text-sm">
          {t.rules.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
        <CheckRow
          checked={draft.agreedRules}
          onChange={(on) => {
            haptic.selection();
            update({ agreedRules: on });
          }}
        >
          {t.agree}
        </CheckRow>
      </Card>

      <Card title={t.privacyTitle}>
        <p className="text-muted mb-2 text-sm">{t.privacyBody}</p>
        <CheckRow
          checked={draft.agreedPrivacy}
          onChange={(on) => {
            haptic.selection();
            update({ agreedPrivacy: on });
          }}
        >
          {t.privacyAgree}
        </CheckRow>
        <Button
          variant="ghost"
          className="mt-1 px-0"
          onClick={() => void navigate('/privacy')}
        >
          {t.privacyLink}
        </Button>
      </Card>

      {error ? (
        <p className="text-danger px-1 text-sm" role="alert">
          {error}
        </p>
      ) : blocker ? (
        <p className="text-muted px-1 text-sm">{blocker}</p>
      ) : null}

      <MainButton
        text={submit.isPending ? t.submitting : t.submit}
        enabled={canSubmit}
        loading={submit.isPending}
        onClick={() => void onSubmit()}
      />
    </>
  );
}
